import { useCallback, useEffect, useState } from 'react';

import { useIntl } from 'react-intl';
import { Button, Modal } from 'react-bootstrap';
import { useInterval, useLocation, useMount, useUpdateEffect } from 'react-use';
import { DateTime, Interval } from 'luxon';
import { floor } from 'lodash';

import { useAppSelector } from 'hooks';
import { useAxios } from 'configs/requests';
import { Error } from 'components/Fetch';
import { MailIcon } from 'assets/icons';
import AvatarNameColor from 'components/UI/AvatarNameColor';
import { selectCurrentGid } from 'store/groups/reducer';

import MessagingPanelFilters from './MessagingPanelFilters';
import MessagingPanelMessages from './MessagingPanelMessages';

const MessagingPanel = () => {
  const intl = useIntl();
  const location = useLocation();
  const user = useAppSelector((state) => state.oauth.data);
  const gid = useAppSelector(selectCurrentGid);
  const intervalPullDefault = (import.meta.env.VITE_INTERVAL_PULL_SECS || 30) * 1000;
  const [show, setShow] = useState(false);
  const [unread, setUnread] = useState(false);

  const loadInterval = { days: 60 };
  const getInitialDateFilter = () =>
    Interval.fromDateTimes(DateTime.now().minus(loadInterval), new Date());
  const getNextInterval = (interval: Interval) =>
    interval.set({
      start: interval.start.minus(loadInterval),
      end: interval.end.minus(loadInterval)
    });

  const [datesFilter, setDatesFilter] = useState(getInitialDateFilter());
  const fullPeriodDaysCount = floor(
    Interval.fromDateTimes(datesFilter.start, new Date()).toDuration('days').days
  );
  const [messages, setMessages] = useState<IMessages[]>([]);
  const [messagesLoading, setMessagesLoading] = useState<boolean>(false);
  const [messagesError, setMessagesError] = useState<boolean>(false);

  const [unreadCount, setUnreadCount] = useState(0);
  const [unreadCountLoading, setUnreadCountLoading] = useState(false);

  const [hideLoadMoreButton, setHideLoadMoreButton] = useState(false);

  // V2 = Azure Communication Services
  const [, getMessagesV2] = useAxios<Array<any>>(
    {
      url: '/clinician-gateway/chat/threads',
      method: 'GET'
    },
    { manual: true }
  );

  const [, getUnreadCountV2] = useAxios(
    {
      url: '/clinician-gateway/chat/threads/unread',
      method: 'GET'
    },
    { manual: true }
  );

  const getUnreadCount = async () => {
    setUnreadCountLoading(true);
    const res = await Promise.allSettled([
      getUnreadCountV2({
        params: {
          ...(gid && gid !== 'all' && { gid })
        }
      })
    ]);

    let count = 0;
    for (const x of res) {
      if (x.status === 'fulfilled') {
        count += Number(x.value.data.unread || x.value.data);
      }
    }

    setUnreadCount(count);
    setUnreadCountLoading(false);
  };

  const unReadCount = Number(unreadCount) > 9 ? '9+' : Number(unreadCount);

  const getMixedMessages = useCallback(
    async (
      dates: Interval,
      updatedUnread: boolean,
      retries: number = 0,
      accumulator: Array<any> = [],
      fetchedMsgCount: number = 0
    ) => {
      let messagesResponse: any[] = [];
      setMessagesLoading(true);
      setMessagesError(false);

      try {
        const messagesV2 = await getMessagesV2({
          params: {
            before: dates.end.toMillis(),
            after: dates.start.toMillis(),
            ...(updatedUnread && { unread: 1 }),
            gid: gid !== 'all' ? gid : undefined
          }
        });
        messagesResponse = messagesResponse.concat(
          messagesV2.data.map((msg) => ({
            ...msg,
            version: 2
          }))
        );
      } catch {
        setMessagesError(true);
      }

      messagesResponse.sort((left, right) => {
        const leftMsgDate = left.message_date
          ? DateTime.fromMillis(Number(left.message_date)).toMillis()
          : DateTime.fromISO(left.lastMessageTimestamp).toMillis();
        const rightMsgDate = right.message_date
          ? DateTime.fromMillis(Number(right.message_date)).toMillis()
          : DateTime.fromISO(right.lastMessageTimestamp).toMillis();
        return rightMsgDate - leftMsgDate;
      });

      const allMessages = accumulator.concat(messagesResponse);
      setMessages(allMessages);

      setMessagesLoading(false);
      setDatesFilter(dates);

      if (fetchedMsgCount < 10 && retries < 1) {
        getMixedMessages(
          getNextInterval(dates),
          updatedUnread,
          retries + 1,
          allMessages,
          fetchedMsgCount + messagesResponse.length
        );
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [gid]
  );

  // hide load more button if message box is scrollable
  useEffect(() => {
    if (!messagesLoading) {
      const panelBody = document.getElementById('messagingPanelBody');
      if (panelBody) {
        const hasVerticalScrollbar = panelBody.scrollHeight > panelBody.clientHeight;
        setHideLoadMoreButton(hasVerticalScrollbar);
      }
    }
  }, [messagesLoading]);

  useEffect(() => {
    if (show) {
      getMixedMessages(getInitialDateFilter(), unread);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getMixedMessages, show]);

  const loadMoreContent = () => {
    if (!messagesLoading && show) {
      getMixedMessages(getNextInterval(datesFilter), unread, Number.MAX_SAFE_INTEGER, messages);
    }
  };

  const handleGetNotificationsWithDelay = () => {
    setTimeout(() => {
      getUnreadCount().catch(() => null);
    }, 2000);
  };

  const handleFilter = (unreadVal: boolean) => {
    setUnread(unreadVal);
    getMixedMessages(getInitialDateFilter(), unreadVal, 0, []);
  };

  const handleGetNotification = () => {
    if (import.meta.env.VITE_DISABLE_INTERVAL_PULL !== 'true') {
      !unreadCountLoading && getUnreadCount().catch(() => null);
      show &&
        !messagesLoading &&
        getMixedMessages(getInitialDateFilter(), unread, Number.MAX_SAFE_INTEGER, []);
    }
  };

  /** refetch every +-5min */
  useInterval(() => {
    handleGetNotification();
  }, intervalPullDefault);

  useMount(() => {
    handleGetNotification();
  });

  useUpdateEffect(() => {
    handleGetNotification();
  }, [gid, location.pathname]);

  const handleOpen = () => {
    setShow(true);
  };

  const handleClose = () => {
    setShow(false);
    setMessages([]);
    setDatesFilter(getInitialDateFilter());
  };

  const retryHandler = () => getMixedMessages(datesFilter, unread, Number.MAX_SAFE_INTEGER, []);

  return (
    <>
      <div className="messagingPanelIconContainer">
        <MailIcon className="icon iconTopNav" style={{ fontSize: '22px' }} onClick={handleOpen} />
        {unreadCount > 0 && (
          <div className="messagingPanelIconCount font-weight-bolder">{unReadCount}</div>
        )}
      </div>
      <Modal
        show={show}
        onHide={handleClose}
        scrollable
        dialogClassName="messagingPanel"
        contentClassName="messagingPanelContent"
      >
        <Modal.Header className="align-items-center">
          <Modal.Title className="d-inline-flex w-100 justify-content-between">
            {intl.formatMessage({ id: 'messagesKey' })}
            <div className="d-flex align-items-center">
              <AvatarNameColor name={user?.displayName || ''} size={30} />
              <span className="ms-2">{user?.displayName}</span>
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="p-0">
          <div className="d-flex flex-column h-100" id="messagingPanelBody">
            <MessagingPanelFilters unread={unread ? '1' : '0'} handleFilter={handleFilter} />
            {messagesError && <Error retry={retryHandler} />}
            <MessagingPanelMessages
              messages={messages}
              handleGetNotificationsWithDelay={handleGetNotificationsWithDelay}
              setShow={setShow}
              loadMoreContent={loadMoreContent}
              getMessageLoading={messagesLoading}
              user={user}
            />
            {!hideLoadMoreButton && (
              <div className="d-flex justify-content-center align-items-center flex-shrink-0">
                <p className="m-0 me-2 text-sm text-gray-550">
                  {intl.formatMessage(
                    { id: 'showingLastNDaysMessagesKey' },
                    { fullPeriodDaysCount }
                  )}
                </p>
                <Button variant="link" onClick={loadMoreContent}>
                  {intl.formatMessage({ id: 'loadMoreKey' })}
                </Button>
              </div>
            )}
          </div>
        </Modal.Body>
        <Modal.Footer className="justify-content-between">
          <Button variant="link" onClick={handleClose} data-test="messages-panel-close-btn">
            {intl.formatMessage({ id: 'closeKey' })}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default MessagingPanel;
